// Navbar.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom'; // Ensure react-router-dom is installed
import './Navbar.css'; // Ensure the path is correct
import logoAr from '../images/logo.png';
import logoFr from '../images/logo fr.jpeg';
import logoEn from '../images/logo eng.jpeg';
import { useLanguage } from '../LanguageContext';

const Navbar = () => {
  const [isActive, setIsActive] = useState(false);
  const navRef = useRef(null);
  const { language, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const currentLogo = language === 'fr' ? logoFr : language === 'en' ? logoEn : logoAr;

  // Function to handle smooth scrolling to a target section
  const handleScroll = (e, targetId) => {
    e.preventDefault(); // Prevent default anchor click behavior
    
    if (location.pathname !== '/') {
      // If not on home page, navigate to home and append hash
      navigate(`/#${targetId}`);
      setIsActive(false);
      return;
    }

    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth' }); // Smooth scroll to section
      setIsActive(false); // Close the menu after clicking
    }
  };

  // Function to toggle the hamburger menu
  const toggleMenu = () => {
    setIsActive(!isActive);
  };

  // Function to close the menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setIsActive(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [navRef]);

  // Handle hash scrolling when navigating back to home page
  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      const targetId = location.hash.substring(1);
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        // Delay slightly to ensure the page has rendered
        const timer = setTimeout(() => {
          targetElement.scrollIntoView({ behavior: 'smooth' });
          // Optional: clear hash from URL to prevent re-scroll on refresh
          // navigate('/', { replace: true });
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [location, navigate]);

  return (
    <nav className="navbar" ref={navRef}>
      <div className="logo">
        {/* Only the logo is a Link to home */}
        <Link to="/" aria-label="Go to Home" onClick={() => setIsActive(false)}>
          <img src={currentLogo} alt="Hajar Ouhsine Logo" />
        </Link>
      </div>

      <ul className={`nav-links ${isActive ? 'active' : ''}`}>
        <li>
          <a href="#about" onClick={(e) => handleScroll(e, 'about')}>
            {language === 'ar' ? 'سيرة ذاتية' : language === 'fr' ? 'Biographie' : 'Biography'}
          </a>
        </li>
        <li>
          <a href="#books" onClick={(e) => handleScroll(e, 'books')} className="highlight-book">
            {language === 'ar' ? 'إصدارات' : language === 'fr' ? 'Publications' : 'Publications'}
          </a>
        </li>
        <li>
          <a href="#videos" onClick={(e) => handleScroll(e, 'videos')}>
            {language === 'ar' ? 'حوارات' : language === 'fr' ? 'Interviews' : 'Interviews'}
          </a>
        </li>
        <li>
          <a href="#interventions" onClick={(e) => handleScroll(e, 'interventions')}>
            {language === 'ar' ? 'مداخلات' : language === 'fr' ? 'Interventions' : 'Interventions'}
          </a>
        </li>
        <li>
          <a href="#articles" onClick={(e) => handleScroll(e, 'articles')}>
            {language === 'ar' ? 'مقالات' : language === 'fr' ? 'Articles' : 'Articles'}
          </a>
        </li>
        <li>
          <a href="#testimonies" onClick={(e) => handleScroll(e, 'testimonies')}>
            {language === 'ar' ? 'شهادات' : language === 'fr' ? 'Témoignages' : 'Testimonies'}
          </a>
        </li>
      </ul>

      <div className="navbar-controls">
        <a href="#contact" className="nav-contact-btn" onClick={(e) => handleScroll(e, 'contact')}>
          {language === 'ar' ? 'تواصل معي' : language === 'fr' ? 'Contact' : 'Contact'}
        </a>
        <div className="lang-buttons-container">
          <button className={`lang-toggle-btn ${language === 'ar' ? 'active' : ''}`} onClick={() => changeLanguage('ar')}>AR</button>
          <span className="lang-separator">|</span>
          <button className={`lang-toggle-btn ${language === 'fr' ? 'active' : ''}`} onClick={() => changeLanguage('fr')}>FR</button>
          <span className="lang-separator">|</span>
          <button className={`lang-toggle-btn ${language === 'en' ? 'active' : ''}`} onClick={() => changeLanguage('en')}>EN</button>
        </div>

        <div
          className={`hamburger ${isActive ? 'toggle' : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle navigation menu"
          aria-expanded={isActive}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === 'Enter') toggleMenu();
          }}
        >
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
